import { baseClient } from "./baseRequest";

/**
 * A config value as stored. Scalars only — the clock flattens each one to a string and
 * coerces it per key. The api accepts any key name, so this is deliberately not a union
 * of known keys.
 */
export type ClockConfigValue = number | string | boolean;

/**
 * One clock-configuration document: either the fleet-wide one (`id: "global"`) or a
 * per-clock override (`id: <clockId>`).
 *
 * `version` is owned by the server and incremented on every write — it is what the
 * clock reports back with its heartbeat, so we can see which clocks picked a change up.
 */
export interface ClockConfig {
  id: string;
  version: number;
  values: Record<string, ClockConfigValue>;
  updatedBy?: string;
  updatedAt?: string;
  createdAt?: string;
}

export interface ClockConfigClient {
  /** The fleet-wide document, or null when nothing has been published yet. */
  getGlobal: () => Promise<ClockConfig | null>;
  updateGlobal: (
    values: Record<string, ClockConfigValue>,
  ) => Promise<ClockConfig>;
  /** Every per-clock override, newest first. */
  listOverrides: () => Promise<ClockConfig[]>;
  /** One clock's override, or null when it has none. */
  getOverride: (clockId: string) => Promise<ClockConfig | null>;
  updateOverride: (
    clockId: string,
    values: Record<string, ClockConfigValue>,
  ) => Promise<ClockConfig>;
  /** Drops the override so the clock follows the global document again. */
  removeOverride: (clockId: string) => Promise<void>;
}

const clockConfigClient: ClockConfigClient = {
  getGlobal: () =>
    baseClient.get<ClockConfig | null>({ url: "/admin/clock-configs/global" }),
  updateGlobal: (values) =>
    baseClient.put<ClockConfig>({
      url: "/admin/clock-configs/global",
      body: { values },
    }),
  listOverrides: () =>
    baseClient.get<ClockConfig[]>({ url: "/admin/clock-configs/overrides" }),
  getOverride: (clockId) =>
    baseClient.get<ClockConfig | null>({
      url: `/admin/clock-configs/${clockId}`,
    }),
  updateOverride: (clockId, values) =>
    baseClient.put<ClockConfig>({
      url: `/admin/clock-configs/${clockId}`,
      body: { values },
    }),
  removeOverride: (clockId) =>
    baseClient.delete<void>({ url: `/admin/clock-configs/${clockId}` }),
};

export default clockConfigClient;
